import { z } from "zod";
import { SpecPolicyFlagFamilyIdSchema, SpecPolicyFlagScopeSchema, SpecPolicyFlagSetSchema } from "./policyFlags.js";
import { IdSchema, IsoTimestampSchema, NonEmptyStringSchema, RuleTraceRefSchema, SourceRefSchema } from "./source.js";
export const SCHEDULE_ENTRIES_SCHEMA_VERSION = "schedule-entries.v1";
export const ScheduleKindSchema = z.enum(["clarification", "exclusion", "assumption"]);
export const ScheduleCategorySchema = z.enum([
    "scope",
    "commercial",
    "technical",
    "coordination",
    "documentation",
    "interface",
    "programme"
]);
export const ScheduleSeveritySchema = z.enum(["critical", "important", "minor"]);
export const ScheduleEntryStatusSchema = z.enum(["proposed", "accepted", "retired"]);
export const ScheduleEntryOriginSchema = z.enum([
    "engine-derived",
    "agent-proposed",
    "agent-generated-user-edited",
    "user-authored",
    "user-edited",
    "agent-edited"
]);
export const ScheduleEntryAuthorTypeSchema = z.enum(["user", "agent", "system"]);
export const ScheduleEntryAddedDuringSchema = z.enum([
    "discovery",
    "specify",
    "user-edit",
    "note-conversion",
    "recheck"
]);
export const ScheduleNoteOutcomeSchema = z.enum([
    "install-excluded",
    "policy-defaulted",
    "needs-user-decision"
]);
const evidenceCount = (value) => (value.sourceRefs?.length ?? 0) + (value.ruleTraceRefs?.length ?? 0);
export const ScheduleEntryProvenanceSchema = z
    .object({
    origin: ScheduleEntryOriginSchema,
    authorType: ScheduleEntryAuthorTypeSchema,
    evidenceSummary: NonEmptyStringSchema,
    sourceRefs: z.array(SourceRefSchema).default([]),
    ruleTraceRefs: z.array(RuleTraceRefSchema).default([]),
    sourceFlagId: IdSchema.optional(),
    sourceFlagFamilyId: SpecPolicyFlagFamilyIdSchema.optional(),
    sourceNoteId: IdSchema.optional(),
    sourceNoteFingerprint: NonEmptyStringSchema.optional(),
    supersedesScheduleEntryId: IdSchema.optional(),
    supersedesSourceNoteFingerprint: NonEmptyStringSchema.optional(),
    model: NonEmptyStringSchema.optional(),
    generatedBy: NonEmptyStringSchema.optional()
})
    .strict()
    .superRefine((value, ctx) => {
    if ([
        "agent-proposed",
        "agent-generated-user-edited",
        "agent-edited",
        "engine-derived"
    ].includes(value.origin) &&
        evidenceCount(value) === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "agent and engine schedule entries require sourceRefs or ruleTraceRefs",
            path: ["sourceRefs"]
        });
    }
});
export const ScheduleEntryHistoryActionSchema = z.enum([
    "created",
    "edited",
    "accepted",
    "restored",
    "retired",
    "proposed-retirement"
]);
export const ScheduleEntryHistoryRecordSchema = z
    .object({
    id: IdSchema,
    action: ScheduleEntryHistoryActionSchema,
    actorType: ScheduleEntryAuthorTypeSchema,
    provenance: ScheduleEntryProvenanceSchema,
    changedAt: IsoTimestampSchema,
    previousStatus: ScheduleEntryStatusSchema.optional(),
    nextStatus: ScheduleEntryStatusSchema.optional(),
    previousBodyText: NonEmptyStringSchema.optional(),
    nextBodyText: NonEmptyStringSchema.optional(),
    reason: NonEmptyStringSchema.optional()
})
    .strict();
const ScheduleEntryShape = {
    schemaVersion: z.literal(SCHEDULE_ENTRIES_SCHEMA_VERSION),
    id: IdSchema,
    projectId: IdSchema,
    category: ScheduleCategorySchema,
    severity: ScheduleSeveritySchema,
    affectedApartmentTypeIds: z.array(IdSchema).default([]),
    scope: SpecPolicyFlagScopeSchema,
    bodyText: NonEmptyStringSchema,
    provenance: ScheduleEntryProvenanceSchema,
    status: ScheduleEntryStatusSchema,
    addedDuring: ScheduleEntryAddedDuringSchema,
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
    history: z.array(ScheduleEntryHistoryRecordSchema).min(1)
};
const makeScheduleEntrySchema = (kind) => z
    .object({
    ...ScheduleEntryShape,
    kind: z.literal(kind)
})
    .strict();
export const ClarificationScheduleEntrySchema = makeScheduleEntrySchema("clarification");
export const ExclusionScheduleEntrySchema = makeScheduleEntrySchema("exclusion");
export const AssumptionScheduleEntrySchema = makeScheduleEntrySchema("assumption");
export const ScheduleEntrySchema = z.discriminatedUnion("kind", [
    ClarificationScheduleEntrySchema,
    ExclusionScheduleEntrySchema,
    AssumptionScheduleEntrySchema
]);
export const ScheduleEntryDraftSchema = z
    .object({
    kind: ScheduleKindSchema,
    category: ScheduleCategorySchema,
    severity: ScheduleSeveritySchema,
    affectedApartmentTypeIds: z.array(IdSchema).default([]),
    scope: SpecPolicyFlagScopeSchema.optional(),
    bodyText: NonEmptyStringSchema,
    evidenceSummary: NonEmptyStringSchema,
    sourceRefs: z.array(SourceRefSchema).default([]),
    ruleTraceRefs: z.array(RuleTraceRefSchema).default([]),
    sourceFlagId: IdSchema.optional(),
    sourceFlagFamilyId: SpecPolicyFlagFamilyIdSchema.optional(),
    sourceNoteId: IdSchema.optional(),
    sourceNoteFingerprint: NonEmptyStringSchema.optional()
})
    .strict()
    .superRefine((value, ctx) => {
    if (evidenceCount(value) === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "schedule draft entries require sourceRefs or ruleTraceRefs",
            path: ["sourceRefs"]
        });
    }
});
export const ScheduleGenerationProviderResponseSchema = z
    .object({
    schemaVersion: z.literal(SCHEDULE_ENTRIES_SCHEMA_VERSION),
    entries: z.array(ScheduleEntryDraftSchema).max(4)
})
    .strict();
export class ScheduleEntryIntegrityError extends Error {
    code = "SCHEDULE_ENTRY_INTEGRITY_ERROR";
    issues;
    constructor(issues) {
        super(formatScheduleEntryIntegrityMessage(issues));
        this.name = "ScheduleEntryIntegrityError";
        this.issues = issues;
    }
}
export function assertScheduleEntryIntegrity(entry, context) {
    const issues = validateScheduleEntryIntegrity(entry, context);
    if (issues.length > 0) {
        throw new ScheduleEntryIntegrityError(issues);
    }
}
export function validateScheduleEntryIntegrity(entry, context) {
    return validateScheduleEvidenceIntegrity(entry, context, ["provenance"]);
}
export function validateScheduleEntryDraftIntegrity(draft, context) {
    return validateScheduleEvidenceIntegrity(draft, context, []);
}
export function expectedScheduleKindForPolicyFlag(flag) {
    if (hasNeedsUserDecisionValue(flag.value)) {
        return "clarification";
    }
    if (isExclusionPolicyFlag(flag)) {
        return "exclusion";
    }
    if (isAssumptionPolicyFlag(flag)) {
        return "assumption";
    }
    return undefined;
}
export function expectedScheduleKindForNoteOutcome(outcome) {
    switch (outcome) {
        case "install-excluded":
            return "exclusion";
        case "policy-defaulted":
            return "assumption";
        case "needs-user-decision":
            return "clarification";
    }
}
const validateScheduleEvidenceIntegrity = (target, context, evidencePath) => {
    const evidence = "provenance" in target ? target.provenance : target;
    const issues = [];
    const parsedFlagSet = parsePolicyFlagSetForIntegrity(context.policyFlagSet, evidence, issues);
    const flags = parsedFlagSet
        ? [...parsedFlagSet.activeFlags, ...parsedFlagSet.supersededFlags]
        : [];
    if (parsedFlagSet) {
        addPolicyFlagSetProjectBindingIssues(parsedFlagSet, context.projectId, issues);
    }
    const projectBoundFlags = parsedFlagSet?.projectId === context.projectId
        ? flags.filter((flag) => flag.scope.projectId === context.projectId)
        : [];
    const flagsById = new Map(projectBoundFlags.map((flag) => [flag.id, flag]));
    const specEvidence = context.specEvidence?.projectId === context.projectId
        ? context.specEvidence
        : undefined;
    const sourceNotes = specEvidence?.sourceNotes ?? [];
    const specRuleTraceRefs = new Set(specEvidence?.ruleTraceRefs ?? []);
    const sourceNotesById = new Map(sourceNotes.map((note) => [note.id, note]));
    const sourceNotesBySourceId = sourceNotesByRef(sourceNotes, (note) => note.sourceIds ?? []);
    const sourceNotesByRuleTraceRef = sourceNotesByRef(sourceNotes, (note) => note.ruleTraceRefs ?? []);
    const sourceIds = new Set([
        ...(context.documents ?? []).map((document) => document.id),
        ...(specEvidence?.sourceIds ?? []),
        ...projectBoundFlags.flatMap((flag) => [
            ...flag.evidenceRefs,
            ...flag.history.flatMap((history) => history.evidenceRefs ?? [])
        ].map((sourceRef) => sourceRef.sourceId))
    ]);
    evidence.sourceRefs.forEach((sourceRef, index) => {
        if (!sourceIds.has(sourceRef.sourceId)) {
            issues.push({
                code: "SOURCE_REF_NOT_FOUND",
                path: [...evidencePath, "sourceRefs", index, "sourceId"],
                refId: sourceRef.sourceId,
                message: `Schedule evidence sourceRef.sourceId "${sourceRef.sourceId}" does not resolve to a project document or policy-flag source.`
            });
        }
    });
    const traceSourceFlag = evidence.ruleTraceRefs
        .map(policyFlagIdFromTraceRef)
        .map((flagId) => (flagId ? flagsById.get(flagId) : undefined))
        .find((flag) => flag !== undefined);
    const sourceFlag = evidence.sourceFlagId
        ? flagsById.get(evidence.sourceFlagId)
        : traceSourceFlag;
    if (evidence.sourceFlagId && !sourceFlag) {
        issues.push({
            code: "SOURCE_FLAG_NOT_FOUND",
            path: [...evidencePath, "sourceFlagId"],
            refId: evidence.sourceFlagId,
            message: `Schedule sourceFlagId "${evidence.sourceFlagId}" does not resolve to an active or superseded policy flag.`
        });
    }
    if (evidence.sourceFlagFamilyId) {
        if (sourceFlag && evidence.sourceFlagFamilyId !== sourceFlag.familyId) {
            issues.push({
                code: "SOURCE_FLAG_FAMILY_MISMATCH",
                path: [...evidencePath, "sourceFlagFamilyId"],
                refId: evidence.sourceFlagFamilyId,
                message: `Schedule sourceFlagFamilyId "${evidence.sourceFlagFamilyId}" does not match sourceFlagId "${sourceFlag.id}" family "${sourceFlag.familyId}".`
            });
        }
        else if (!sourceFlag &&
            !projectBoundFlags.some((flag) => flag.familyId === evidence.sourceFlagFamilyId)) {
            issues.push({
                code: "SOURCE_FLAG_FAMILY_NOT_FOUND",
                path: [...evidencePath, "sourceFlagFamilyId"],
                refId: evidence.sourceFlagFamilyId,
                message: `Schedule sourceFlagFamilyId "${evidence.sourceFlagFamilyId}" does not resolve to an active or superseded policy flag family.`
            });
        }
    }
    evidence.ruleTraceRefs.forEach((ruleTraceRef, index) => {
        if (!ruleTraceRefResolves(ruleTraceRef, flagsById, specRuleTraceRefs)) {
            issues.push({
                code: "RULE_TRACE_REF_NOT_FOUND",
                path: [...evidencePath, "ruleTraceRefs", index],
                refId: ruleTraceRef,
                message: `Schedule ruleTraceRef "${ruleTraceRef}" does not resolve to a real policy flag trace.`
            });
        }
    });
    const citedSourceNotes = citedScheduleSourceNotes({
        evidence,
        sourceNotesById,
        sourceNotesBySourceId,
        sourceNotesByRuleTraceRef,
        contextProjectId: context.projectId,
        specEvidence
    });
    const sourceNote = evidence.sourceNoteId
        ? sourceNotesById.get(evidence.sourceNoteId)
        : citedSourceNotes[0];
    if (evidence.sourceNoteId && !sourceNote) {
        issues.push({
            code: "SOURCE_NOTE_NOT_FOUND",
            path: [...evidencePath, "sourceNoteId"],
            refId: evidence.sourceNoteId,
            message: `Schedule sourceNoteId "${evidence.sourceNoteId}" does not resolve to harvested specification note evidence in this project.`
        });
    }
    addUnresolvedCitedScheduleNoteIssues({
        evidence,
        evidencePath,
        sourceNotesBySourceId,
        sourceNotesByRuleTraceRef,
        contextProjectId: context.projectId,
        issues
    });
    if (sourceFlag) {
        const expectedKind = expectedScheduleKindForPolicyFlag(sourceFlag);
        if (expectedKind && target.kind !== expectedKind) {
            issues.push({
                code: "SCHEDULE_KIND_FLAG_MISMATCH",
                path: ["kind"],
                refId: sourceFlag.id,
                message: `Schedule kind "${target.kind}" does not match policy flag "${sourceFlag.id}" outcome; expected "${expectedKind}".`
            });
        }
    }
    for (const citedSourceNote of citedSourceNotes) {
        const expectedKind = expectedScheduleKindForNoteOutcome(citedSourceNote.outcome);
        if (target.kind !== expectedKind) {
            issues.push({
                code: "SCHEDULE_KIND_NOTE_MISMATCH",
                path: ["kind"],
                refId: citedSourceNote.id,
                message: `Schedule kind "${target.kind}" does not match specification note "${citedSourceNote.id}" outcome; expected "${expectedKind}".`
            });
        }
    }
    return issues;
};
const parsePolicyFlagSetForIntegrity = (policyFlagSet, evidence, issues) => {
    if (policyFlagSet === null || policyFlagSet === undefined) {
        return undefined;
    }
    const parsed = SpecPolicyFlagSetSchema.safeParse(policyFlagSet);
    if (parsed.success) {
        return parsed.data;
    }
    if (evidence.sourceFlagId ||
        evidence.sourceFlagFamilyId ||
        evidence.ruleTraceRefs.some((ruleTraceRef) => ruleTraceRef.startsWith("policy-flag:"))) {
        issues.push({
            code: "POLICY_FLAG_SET_INVALID",
            path: ["policyFlagSet"],
            message: "Project policyFlagSet is present but does not parse."
        });
    }
    return undefined;
};
const addPolicyFlagSetProjectBindingIssues = (policyFlagSet, projectId, issues) => {
    if (policyFlagSet.projectId !== projectId) {
        issues.push({
            code: "POLICY_FLAG_SET_PROJECT_MISMATCH",
            path: ["policyFlagSet", "projectId"],
            refId: policyFlagSet.projectId,
            message: `Project policyFlagSet belongs to project "${policyFlagSet.projectId}", not current project "${projectId}".`
        });
    }
    [
        ["activeFlags", policyFlagSet.activeFlags],
        ["supersededFlags", policyFlagSet.supersededFlags]
    ].forEach(([collectionName, flags]) => {
        flags.forEach((flag, index) => {
            if (flag.scope.projectId !== projectId) {
                issues.push({
                    code: "POLICY_FLAG_SCOPE_PROJECT_MISMATCH",
                    path: ["policyFlagSet", collectionName, index, "scope", "projectId"],
                    refId: flag.scope.projectId,
                    message: `Policy flag "${flag.id}" belongs to project "${flag.scope.projectId}", not current project "${projectId}".`
                });
            }
        });
    });
    policyFlagSet.canonicalActiveValueVector.forEach((entry, index) => {
        if (entry.scope.projectId !== projectId) {
            issues.push({
                code: "POLICY_FLAG_VECTOR_SCOPE_PROJECT_MISMATCH",
                path: [
                    "policyFlagSet",
                    "canonicalActiveValueVector",
                    index,
                    "scope",
                    "projectId"
                ],
                refId: entry.scope.projectId,
                message: `Policy flag canonicalActiveValueVector entry for "${entry.familyId}" belongs to project "${entry.scope.projectId}", not current project "${projectId}".`
            });
        }
    });
};
const ruleTraceRefResolves = (ruleTraceRef, flagsById, specRuleTraceRefs) => {
    if (specRuleTraceRefs.has(ruleTraceRef)) {
        return true;
    }
    if (flagsById.has(ruleTraceRef)) {
        return true;
    }
    if (!ruleTraceRef.startsWith("policy-flag:")) {
        return false;
    }
    return flagsById.has(ruleTraceRef.slice("policy-flag:".length));
};
const sourceNotesByRef = (sourceNotes, refsFor) => {
    const result = new Map();
    for (const note of sourceNotes) {
        for (const ref of refsFor(note)) {
            const notesForRef = result.get(ref) ?? [];
            notesForRef.push(note);
            result.set(ref, notesForRef);
        }
    }
    return result;
};
const citedScheduleSourceNotes = (input) => {
    if (!input.specEvidence || input.specEvidence.projectId !== input.contextProjectId) {
        return [];
    }
    return uniqueSourceNotes([
        ...(input.evidence.sourceNoteId
            ? [input.sourceNotesById.get(input.evidence.sourceNoteId)].filter((note) => note !== undefined)
            : []),
        ...input.evidence.sourceRefs.flatMap((sourceRef) => input.sourceNotesBySourceId.get(sourceRef.sourceId) ?? []),
        ...input.evidence.ruleTraceRefs.flatMap((ruleTraceRef) => input.sourceNotesByRuleTraceRef.get(ruleTraceRef) ?? [])
    ]);
};
const addUnresolvedCitedScheduleNoteIssues = (input) => {
    input.evidence.sourceRefs.forEach((sourceRef, index) => {
        if (isScheduleSpecNoteSourceRef(sourceRef, input.contextProjectId) &&
            !input.sourceNotesBySourceId.has(sourceRef.sourceId)) {
            input.issues.push({
                code: "SOURCE_NOTE_NOT_FOUND",
                path: [...input.evidencePath, "sourceRefs", index, "sourceId"],
                refId: sourceRef.sourceId,
                message: `Schedule sourceRef.sourceId "${sourceRef.sourceId}" cites specification-note evidence but does not resolve to harvested note evidence in this project.`
            });
        }
    });
    input.evidence.ruleTraceRefs.forEach((ruleTraceRef, index) => {
        if (isScheduleSpecNoteRuleTraceRef(ruleTraceRef) &&
            !input.sourceNotesByRuleTraceRef.has(ruleTraceRef)) {
            input.issues.push({
                code: "SOURCE_NOTE_NOT_FOUND",
                path: [...input.evidencePath, "ruleTraceRefs", index],
                refId: ruleTraceRef,
                message: `Schedule ruleTraceRef "${ruleTraceRef}" cites specification-note evidence but does not resolve to harvested note evidence in this project.`
            });
        }
    });
};
const uniqueSourceNotes = (sourceNotes) => {
    const seen = new Set();
    const result = [];
    for (const note of sourceNotes) {
        const key = `${note.id}\u0000${note.contentFingerprint ?? ""}`;
        if (seen.has(key)) {
            continue;
        }
        seen.add(key);
        result.push(note);
    }
    return result;
};
const isScheduleSpecNoteSourceRef = (sourceRef, projectId) => sourceRef.sourceId.startsWith(`spec-row:${projectId}:`) ||
    (sourceRef.sourceKind === "note" &&
        sourceRef.authorityClass === "proposal_forge_generated");
const isScheduleSpecNoteRuleTraceRef = (ruleTraceRef) => ruleTraceRef.startsWith("spec-note-trace:");
const policyFlagIdFromTraceRef = (ruleTraceRef) => ruleTraceRef.startsWith("policy-flag:")
    ? ruleTraceRef.slice("policy-flag:".length)
    : undefined;
const hasNeedsUserDecisionValue = (value) => value.kind === "mode" && value.mode === "needs_user_decision";
const isExclusionPolicyFlag = (flag) => {
    if (flag.familyId.startsWith("supply-scope.") &&
        flag.value.kind === "boolean" &&
        flag.value.value === false) {
        return true;
    }
    if (flag.familyId === "item-class-scope.scope" &&
        flag.value.kind === "mode" &&
        ["exclude_from_bom", "by_others", "not_required", "explicit_by_others"].includes(flag.value.mode)) {
        return true;
    }
    if ((flag.familyId === "network-power.poe-switch-inclusion" ||
        flag.familyId === "network-power.central-panel-enclosure-inclusion") &&
        flag.value.kind === "mode" &&
        ["not_required", "explicit_by_others"].includes(flag.value.mode)) {
        return true;
    }
    if (flag.familyId.startsWith("sensor-policy.") &&
        flag.value.kind === "mode" &&
        flag.value.mode === "by_others") {
        return true;
    }
    return (flag.familyId === "netmaster-capability.no-trigger-posture" &&
        flag.value.kind === "mode" &&
        flag.value.mode === "advisory_lean_exclude");
};
const isAssumptionPolicyFlag = (flag) => {
    if (flag.familyId === "netmaster-capability.no-trigger-posture" &&
        flag.value.kind === "mode" &&
        flag.value.mode === "advisory_lean_include") {
        return true;
    }
    return flag.provenance === "standard-default" || flag.confidence.label === "low";
};
const formatScheduleEntryIntegrityMessage = (issues) => {
    const summary = issues.map((issue) => issue.message).join("; ");
    return summary
        ? `Schedule entry evidence integrity failed: ${summary}`
        : "Schedule entry evidence integrity failed.";
};
//# sourceMappingURL=schedules.js.map